import './ImageField.scss';

import * as React from 'react';

import cx from 'classnames';
import { useDispatch } from 'react-redux';

import { ImageUploadContext } from '~analytics/adapters/ImageEventAdapterTypes';
import { RemoveImageEventAdapter } from '~analytics/adapters/RemoveImageEventAdapter';
import ImagePreviewer from '~shared/components/ImageField/ImagePreviewer';
import { Spinner } from '~shared/components/Spinner/Spinner';
import { useUploadImage } from '~teacher/hook/upload/useUploadImage';
import { handleUnexpectedError } from '~teacher/slices/quiz/quizAppThunks';

import { DropZoneContext } from '../../providers/DropZoneProvider';
import ImageFieldClear from './ImageFieldClear';
import { ImageFieldDropZone, OnAddFilesHandler } from './ImageFieldDropZone';
import ImageFieldPlaceholder from './ImageFieldPlaceholder';

export interface Props {
  className?: string;
  id?: string;
  labelText: string;
  clearLabel: string;
  multiple?: boolean;
  tabIndex?: number;
  disabled?: boolean;
  accept?: string[];
  size?: 'default' | 'inline';
  initialImages?: string[];
  onUpdateImage: (image: string) => void;
  addImageIntercept?: () => void;
  onBeginUploadingImage?: () => void;
  onFinishUploadingImage?: () => void;
  context: ImageUploadContext;
}

export default function ImageField({
  size = 'default',
  onFinishUploadingImage,
  onUpdateImage,
  className,
  labelText,
  clearLabel,
  initialImages,
  onBeginUploadingImage,
  addImageIntercept,
  context,
  ...props
}: Props) {
  const dispatch = useDispatch();

  const [images, setImages] = React.useState<File[] | string[]>(
    initialImages ?? []
  );
  const [hasImage, setHasImage] = React.useState(!!initialImages);
  const [buildingPreview, setBuildingPreview] = React.useState(false);
  const [imagePreview, setImagePreview] = React.useState<string | ArrayBuffer>(
    null
  );

  const previewFile = async (file: File) => {
    /**
     * @todo Do something more helpful if `file` is undefined due to selecting
     *       an invalid file type.
     */
    const reader = new FileReader();
    setBuildingPreview(true);
    reader.readAsDataURL(file);
    reader.onloadend = () => {};
  };

  const fieldClass = cx('relative rounded-2', 'image-field', {
    'w-70 h-70 bg-white': size === 'default' && hasImage,
    'is-inline flex-none py-2 px-4': size === 'inline',
    'w-full flex-1': size === 'default' && !hasImage,
    'md:w-20 h-12 mix-multiply': size === 'inline' && !hasImage,
    'w-60 h-60 p-4': size === 'inline' && hasImage,
    'has-image': hasImage,
    [className as string]: !!className,
  });

  const previewClass = cx(
    'absolute inset-0 flex m-4 text-grey-400 bg-white-a66 rounded-1 shadow-outline-px'
  );

  const imgUploadedSrc = imagePreview?.toString();
  const imgURL = images?.length > 0 ? images[0] : null;
  const imgPreviewSrc = imgUploadedSrc || imgURL;

  const uploadImageMutation = useUploadImage({
    context: context,
  });

  const handleOnAddFiles: OnAddFilesHandler = ({ files, addMethod }) => {
    if (files.length) {
      previewFile(files[0]);
      setImages(files);
      setHasImage(true);

      onBeginUploadingImage?.();

      uploadImageMutation.mutate(
        {
          files: files,
          addMethod: addMethod,
        },
        {
          onSuccess: (data) => {
            onUpdateImage(data);
            onFinishUploadingImage?.();
          },
          onError: (err: unknown) => {
            setImagePreview(null);
            setHasImage(false);
            setBuildingPreview(false);
            setImages([]);
            onUpdateImage(null);
            onFinishUploadingImage?.();

            console.error(err);
            dispatch(handleUnexpectedError(err));
          },
        }
      );
    }
  };

  return (
    <div className={fieldClass} data-image-field>
      <DropZoneContext.Consumer>
        {(dropZoneActive) => (
          <>
            <ImageFieldDropZone
              {...props}
              ariaLabel={labelText}
              isZoneActive={dropZoneActive}
              onAddFiles={handleOnAddFiles}
              addImageIntercept={addImageIntercept}
              accept={['image/*']}
              disabled={hasImage}
            >
              {!hasImage && (
                <ImageFieldPlaceholder
                  size={size}
                  isZoneActive={dropZoneActive}
                />
              )}
            </ImageFieldDropZone>
            {hasImage && (
              <div
                className={
                  dropZoneActive
                    ? `${previewClass} pointer-events-none`
                    : previewClass
                }
                data-image-field-preview
                data-testid="image-field-preview"
              >
                <div className="flex items-center justify-center flex-1 rounded-1 overflow-hidden">
                  {!buildingPreview ? (
                    <ImagePreviewer
                      imageStyle="w-full h-full object-contain"
                      imageURL={imgPreviewSrc as string}
                      containerStyle="w-full h-full"
                    />
                  ) : (
                    <Spinner />
                  )}
                </div>

                <div className="absolute top-0 right-0 pointer-events-auto">
                  <ImageFieldClear
                    onClick={() => {
                      setImages(null);
                      setHasImage(false);

                      // TODO: cancel uploading the image if there is an image upload in progress.
                      onFinishUploadingImage?.();
                      onUpdateImage(null);
                      new RemoveImageEventAdapter({
                        context: context,
                      }).sendAnalytics();
                    }}
                    aria-label={clearLabel}
                  />
                </div>
              </div>
            )}
          </>
        )}
      </DropZoneContext.Consumer>
    </div>
  );
}
